import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faEye, faLock, faUserShield } from '@fortawesome/free-solid-svg-icons';

function PrivacyPage() {
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="max-w-7xl mx-auto px-4 py-16">
                    <div className="text-center">
                        <div className="flex justify-center mb-6">
                            <FontAwesomeIcon icon={faShieldAlt} className="text-6xl text-blue-200" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold mb-4">
                            Política de Privacidad
                        </h1>
                        <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                            Tu privacidad es fundamental para nosotros. Conoce cómo protegemos y gestionamos tus datos personales.
                        </p>
                        <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                                <FontAwesomeIcon icon={faEye} className="text-blue-200" />
                                <span>Última actualización: {new Date().toLocaleDateString('es-ES')}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <FontAwesomeIcon icon={faLock} className="text-blue-200" />
                                <span>Datos protegidos por GDPR</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 py-12">
                <div className="bg-white rounded-lg shadow-lg p-8">
                    {/* Introduction */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                            <FontAwesomeIcon icon={faUserShield} className="mr-3 text-blue-600" />
                            1. Introducción
                        </h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            PetitPrime ("nosotros", "nuestro", "la Plataforma") se compromete a proteger la privacidad de sus usuarios.
                            Esta Política de Privacidad describe cómo recopilamos, utilizamos, almacenamos y protegemos la información
                            personal que nos proporcionas a través de nuestra plataforma de fidelización digital.
                        </p>
                        <p className="text-gray-600 leading-relaxed">
                            Al utilizar nuestros servicios, aceptas las prácticas descritas en esta política. Si no estás de acuerdo
                            con alguna parte de esta política, por favor no utilices nuestros servicios.
                        </p>
                    </section>

                    {/* Information We Collect */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">2. Información que Recopilamos</h2>

                        <h3 className="text-xl font-semibold text-gray-700 mb-4">2.1 Información Personal</h3>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>Nombre completo y apellidos</li>
                            <li>Dirección de correo electrónico</li>
                            <li>Número de teléfono</li>
                            <li>Dirección postal</li>
                            <li>Fecha de nacimiento</li>
                            <li>Información de la empresa (para usuarios comerciales)</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-gray-700 mb-4">2.2 Información de Uso</h3>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>Datos de navegación y uso de la plataforma</li>
                            <li>Información sobre transacciones y compras</li>
                            <li>Datos de fidelización y puntos acumulados</li>
                            <li>Preferencias y comportamiento de compra</li>
                            <li>Información técnica del dispositivo</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-gray-700 mb-4">2.3 Cookies y Tecnologías Similares</h3>
                        <p className="text-gray-600 leading-relaxed">
                            Utilizamos cookies y tecnologías similares para mejorar tu experiencia, analizar el tráfico del sitio
                            y personalizar el contenido. Puedes gestionar tus preferencias de cookies en cualquier momento.
                        </p>
                    </section>

                    {/* How We Use Information */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">3. Cómo Utilizamos tu Información</h2>
                        <ul className="list-disc list-inside text-gray-600 space-y-3">
                            <li>Proporcionar y mantener nuestros servicios de fidelización</li>
                            <li>Gestionar tu cuenta y procesar transacciones</li>
                            <li>Enviar comunicaciones relacionadas con el servicio</li>
                            <li>Personalizar tu experiencia y ofrecer recomendaciones</li>
                            <li>Analizar el uso de la plataforma para mejoras</li>
                            <li>Cumplir con obligaciones legales y regulatorias</li>
                            <li>Prevenir fraudes y garantizar la seguridad</li>
                        </ul>
                    </section>

                    {/* Information Sharing */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">4. Compartir Información</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto en las siguientes circunstancias:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-3">
                            <li><strong>Con tu consentimiento:</strong> Cuando nos autorices explícitamente</li>
                            <li><strong>Proveedores de servicios:</strong> Empresas que nos ayudan a operar la plataforma</li>
                            <li><strong>Cumplimiento legal:</strong> Cuando sea requerido por ley</li>
                            <li><strong>Protección de derechos:</strong> Para proteger nuestros derechos y seguridad</li>
                        </ul>
                    </section>

                    {/* Data Security */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">5. Seguridad de Datos</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger tu información personal:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-3">
                            <li>Encriptación de datos en tránsito y en reposo</li>
                            <li>Acceso restringido a información personal</li>
                            <li>Monitoreo continuo de seguridad</li>
                            <li>Copias de seguridad regulares</li>
                            <li>Formación del personal en seguridad</li>
                        </ul>
                    </section>

                    {/* Your Rights */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">6. Tus Derechos</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Según el RGPD, tienes los siguientes derechos sobre tus datos personales:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-3">
                            <li><strong>Acceso:</strong> Solicitar información sobre los datos que tenemos</li>
                            <li><strong>Rectificación:</strong> Corregir datos inexactos o incompletos</li>
                            <li><strong>Supresión:</strong> Solicitar la eliminación de tus datos</li>
                            <li><strong>Portabilidad:</strong> Recibir tus datos en formato estructurado</li>
                            <li><strong>Limitación:</strong> Restringir el procesamiento de tus datos</li>
                            <li><strong>Oposición:</strong> Oponerte al procesamiento de tus datos</li>
                        </ul>
                    </section>

                    {/* Data Retention */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">7. Retención de Datos</h2>
                        <p className="text-gray-600 leading-relaxed">
                            Conservamos tu información personal únicamente durante el tiempo necesario para cumplir con los
                            propósitos descritos en esta política, o según lo requiera la ley. Cuando ya no necesitemos
                            tus datos, los eliminaremos de forma segura.
                        </p>
                    </section>

                    {/* International Transfers */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">8. Transferencias Internacionales</h2>
                        <p className="text-gray-600 leading-relaxed">
                            Tus datos pueden ser procesados en países fuera del Espacio Económico Europeo (EEE).
                            Cuando esto ocurra, garantizamos que se apliquen las medidas de protección adecuadas
                            según las leyes de protección de datos aplicables.
                        </p>
                    </section>

                    {/* Children's Privacy */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">9. Privacidad de Menores</h2>
                        <p className="text-gray-600 leading-relaxed">
                            Nuestros servicios no están dirigidos a menores de 16 años. No recopilamos intencionalmente
                            información personal de menores de 16 años. Si eres padre o tutor y crees que tu hijo nos
                            ha proporcionado información personal, contáctanos inmediatamente.
                        </p>
                    </section>

                    {/* Changes to Policy */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">10. Cambios en esta Política</h2>
                        <p className="text-gray-600 leading-relaxed">
                            Podemos actualizar esta Política de Privacidad ocasionalmente. Te notificaremos cualquier
                            cambio significativo por correo electrónico o mediante un aviso en nuestra plataforma.
                            Te recomendamos revisar esta política periódicamente.
                        </p>
                    </section>

                    {/* Contact Information */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">11. Información de Contacto</h2>
                        <div className="bg-gray-50 rounded-lg p-6">
                            <p className="text-gray-600 mb-4">
                                Si tienes preguntas sobre esta Política de Privacidad o sobre el tratamiento de tus datos personales,
                                puedes contactarnos:
                            </p>
                            <div className="space-y-2 text-gray-600">
                                <p><strong>Email:</strong> privacy@petitstores.com</p>
                                <p><strong>Teléfono:</strong> +34 900 123 456</p>
                                <p><strong>Dirección:</strong> Barcelona, España</p>
                                <p><strong>Delegado de Protección de Datos:</strong> dpo@petitstores.com</p>
                            </div>
                        </div>
                    </section>

                    {/* CTA Section */}
                    <section className="text-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            ¿Tienes preguntas sobre privacidad?
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Nuestro equipo está aquí para ayudarte con cualquier consulta sobre la protección de tus datos.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/contact"
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
                            >
                                Contactar Soporte
                            </Link>
                            <Link
                                to="/terminos"
                                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
                            >
                                Ver Términos y Condiciones
                            </Link>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default PrivacyPage; 